/**
 * ErrorBoundary - catches an error thrown while rendering the pages below it
 * and shows a short message with a reload button, instead of the blank white
 * page React leaves when a render throws.
 *
 * `resetKey`: when it changes (e.g. the route), the boundary clears its
 * error and renders its children again, so moving to another page works
 * without a reload.
 */
import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Clears a caught error when it changes. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/** What the user sees in place of the page that failed to render. */
export const ErrorFallback: React.FC<{ onReload?: () => void }> = ({ onReload }) => {
  const { t } = useTranslation();
  const reload = onReload ?? (() => window.location.reload());
  return (
    <Box
      role="alert"
      data-testid="error-boundary"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 6, px: 2 }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" component="h1" sx={{ mb: 1 }}>
          {t('errorBoundary.title', 'Something went wrong')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'errorBoundary.message',
            'This page could not be shown. Reload the page to try again. If it happens again, tell your administrator what you were doing.',
          )}
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={reload}>
          {t('errorBoundary.reload', 'Reload page')}
        </Button>
      </Paper>
    </Box>
  );
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Kept in the browser console for support; the user sees the fallback.
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) return <ErrorFallback />;
    return this.props.children;
  }
}

export default ErrorBoundary;
